import { API_BASE } from './client';
import type {
  BlockDeltaEvent,
  BlockStartEvent,
  BlockStopEvent,
  ErrorEvent as RunErrorEvent,
  RunFinishEvent,
  RunStartEvent,
  SSEEventName,
} from '../types/sse';

export interface RunStreamHandlers {
  onRunStart?: (data: RunStartEvent) => void;
  onBlockStart?: (data: BlockStartEvent) => void;
  onBlockDelta?: (data: BlockDeltaEvent) => void;
  onBlockStop?: (data: BlockStopEvent) => void;
  onRunFinish?: (data: RunFinishEvent) => void;
  onError?: (data: RunErrorEvent) => void;
}

interface ParsedEvent {
  name: string;
  data: string;
}

/**
 * Stream a run via SSE. Returns when the connection ends (server-closed,
 * client-aborted via signal, or network error).
 *
 * Per backend contract: aborting via signal closes the SSE connection but
 * does NOT cancel the server-side run. The run continues and its result is
 * persisted to the conversation.
 */
export async function streamRun(
  conversationId: string,
  token: string | undefined,
  handlers: RunStreamHandlers,
  signal: AbortSignal,
): Promise<void> {
  const headers = new Headers({
    Accept: 'text/event-stream',
    'Content-Type': 'application/json',
  });
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE}/v1/conversations/${conversationId}/runs`, {
    method: 'POST',
    headers,
    signal,
    body: '{}',
  });

  if (!res.ok) {
    throw new Error(`Run start failed: HTTP ${res.status}`);
  }
  if (!res.body) {
    throw new Error('Run start failed: no response body');
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE event boundary: blank line (\n\n).
      let boundary: number;
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const rawEvent = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const parsed = parseEvent(rawEvent);
        if (parsed) dispatch(parsed, handlers);
      }
    }
    // Flush trailing event if any (defensive — well-formed SSE always trails \n\n).
    if (buffer.trim()) {
      const parsed = parseEvent(buffer);
      if (parsed) dispatch(parsed, handlers);
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // no-op
    }
  }
}

function parseEvent(raw: string): ParsedEvent | null {
  let name = 'message';
  const dataLines: string[] = [];
  for (const line of raw.split('\n')) {
    if (!line || line.startsWith(':')) continue; // comment / keepalive
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') name = value;
    else if (field === 'data') dataLines.push(value);
    // Ignore id, retry, and unknown fields for now.
  }
  if (dataLines.length === 0) return null;
  return { name, data: dataLines.join('\n') };
}

function dispatch(evt: ParsedEvent, h: RunStreamHandlers): void {
  let payload: unknown;
  try {
    payload = JSON.parse(evt.data);
  } catch (e) {
    console.warn('SSE: failed to parse event data', evt, e);
    return;
  }

  const name = evt.name as SSEEventName;
  switch (name) {
    case 'run_start':
      h.onRunStart?.(payload as RunStartEvent);
      return;
    case 'block_start':
      h.onBlockStart?.(payload as BlockStartEvent);
      return;
    case 'block_delta':
      h.onBlockDelta?.(payload as BlockDeltaEvent);
      return;
    case 'block_stop':
      h.onBlockStop?.(payload as BlockStopEvent);
      return;
    case 'run_finish':
      h.onRunFinish?.(payload as RunFinishEvent);
      return;
    case 'error':
      h.onError?.(payload as RunErrorEvent);
      return;
    default:
      console.warn('SSE: unknown event name', evt.name);
  }
}
